import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { apiErr, isPaywall } from "@/lib/api";
import { MicButton } from "@/components/MicButton";
import { Container, PageHeader, Spinner, PaywallNotice, GenerateButton } from "@/components/shared";
import { toast } from "sonner";
import {
  Scale, Search, ShieldAlert, Briefcase, Home, Store, AlertTriangle,
  MessageSquareWarning, FileWarning, IdCard, HardHat, GraduationCap,
  ShoppingCart, ShieldX, Sparkles, Info, ChevronRight, ArrowRight,
} from "lucide-react";

const ICONS = {
  ShieldAlert, Briefcase, Home, Store, AlertTriangle, MessageSquareWarning,
  FileWarning, IdCard, HardHat, GraduationCap, ShoppingCart, ShieldX, Scale,
};

export default function Rights() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [query, setQuery] = useState("");
  const [matches, setMatches] = useState(null);
  const [searching, setSearching] = useState(false);
  const [situation, setSituation] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [paywall, setPaywall] = useState(false);

  useEffect(() => {
    api.get("/rights").then(({ data }) => setData(data)).catch((e) => toast.error(apiErr(e)));
  }, []);

  const runSearch = async () => {
    if (!query.trim()) return;
    setSearching(true); setMatches(null);
    try {
      const { data } = await api.post("/ai/rights/search", { query });
      setMatches(data.matches || []);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSearching(false); }
  };

  const analyzeSituation = async () => {
    if (!situation.trim()) return;
    setBusy(true); setPaywall(false); setResult(null);
    try {
      const { data } = await api.post("/ai/rights/situation", { situation });
      setResult(data);
    } catch (e) {
      if (isPaywall(e)) setPaywall(true); else toast.error(apiErr(e));
    } finally { setBusy(false); }
  };

  const titleFor = (slug) => data?.sheets?.find((s) => s.slug === slug)?.title || slug;

  const RESULT_SECTIONS = [
    { key: "applicable_rules", label: t("pages.rights.sec_rules") },
    { key: "rights_concerned", label: t("pages.rights.sec_rights") },
    { key: "evidence", label: t("pages.rights.sec_evidence") },
    { key: "steps", label: t("pages.rights.sec_steps") },
    { key: "organizations", label: t("pages.rights.sec_orgs") },
  ];

  return (
    <Container>
      <PageHeader eyebrow={t("pages.rights.eyebrow")} title={t("pages.rights.title")}
        subtitle={t("pages.rights.subtitle")} />

      <div data-testid="rights-disclaimer" className="mb-6 flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-4">
        <Info size={18} className="text-gold shrink-0 mt-0.5" />
        <p className="text-sm text-white/60 leading-relaxed">{data?.disclaimer || t("pages.rights.disclaimer")}</p>
      </div>

      <button data-testid="rights-help-cta" onClick={() => navigate("/rights/aide")}
        className="w-full mb-6 flex items-center justify-between gap-3 rounded-2xl border border-gold/30 bg-gradient-to-r from-[#BF953F]/15 to-transparent p-5 text-left hover:from-[#BF953F]/25 transition-colors">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gold/15 border border-gold/25 flex items-center justify-center">
            <ShieldAlert size={22} className="text-gold" strokeWidth={1.5} />
          </div>
          <div>
            <p className="font-display text-base">{t("pages.rights.victimCta")}</p>
            <p className="text-white/50 text-sm">{t("pages.rights.victimSub")}</p>
          </div>
        </div>
        <ChevronRight size={20} className="text-gold shrink-0" />
      </button>

      {/* Recherche intelligente */}
      <div className="card-surface p-5 mb-6">
        <label className="text-sm text-white/70 mb-2 block flex items-center gap-2"><Search size={16} className="text-gold" /> {t("pages.rights.smartSearch")}</label>
        <div className="flex flex-wrap gap-2">
          <input data-testid="rights-search-input" value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && runSearch()}
            placeholder={t("pages.rights.searchPlaceholder")}
            className="flex-1 min-w-0 basis-full sm:basis-0 bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2.5 outline-none text-sm placeholder:text-white/30" />
          <GenerateButton data-testid="rights-search-button" className="w-full sm:w-auto shrink-0" onClick={runSearch} disabled={searching || !query.trim()}>
            {searching ? "..." : <>{t("pages.rights.find")} <ArrowRight size={16} /></>}
          </GenerateButton>
        </div>
        {matches && (
          <div className="mt-4 space-y-2" data-testid="rights-search-results">
            {matches.length === 0 && <p className="text-white/40 text-sm">{t("pages.rights.noMatch")}</p>}
            {matches.map((m) => (
              <button key={m.slug} data-testid={`rights-match-${m.slug}`} onClick={() => navigate(`/rights/${m.slug}`)}
                className="w-full text-left flex items-center justify-between gap-3 rounded-xl border border-gold/25 bg-gold/[0.06] px-4 py-3 hover:bg-gold/[0.1] transition-colors">
                <div>
                  <p className="text-sm text-white font-medium">{titleFor(m.slug)}</p>
                  {m.reason && <p className="text-xs text-white/50 mt-0.5">{m.reason}</p>}
                </div>
                <ChevronRight size={18} className="text-gold shrink-0" />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Assistant : ma situation est-elle concernée ? */}
      <div className="card-surface p-5 mb-8">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles size={18} className="text-gold" />
          <h3 className="font-display text-lg">{t("pages.rights.situationTitle")}</h3>
        </div>
        <p className="text-white/45 text-sm mb-3">{t("pages.rights.situationDesc")}</p>
        <textarea data-testid="rights-situation-input" rows={4} value={situation}
          onChange={(e) => setSituation(e.target.value)}
          placeholder={t("pages.rights.situationPlaceholder")}
          className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2.5 outline-none resize-none text-sm placeholder:text-white/30" />
        <div className="flex items-center justify-between gap-3 mt-3">
          <MicButton testid="rights-situation-mic" value={situation} onChange={setSituation} />
          <GenerateButton data-testid="rights-situation-button" onClick={analyzeSituation} disabled={busy || !situation.trim()}>
            <Scale size={16} /> {t("pages.rights.analyzeMy")}
          </GenerateButton>
        </div>
        {busy && <Spinner label={t("pages.rights.spinner")} />}
        {paywall && <PaywallNotice />}
        {result && (
          <div className="mt-4 space-y-4" data-testid="rights-situation-result">
            {RESULT_SECTIONS.map((s) => {
              const items = result[s.key] || [];
              if (!items.length) return null;
              return (
                <div key={s.key}>
                  <p className="text-gold text-xs uppercase tracking-[0.15em] mb-2">{s.label}</p>
                  <ul className="space-y-1.5">
                    {items.map((it, i) => (
                      <li key={i} className="text-white/80 text-sm leading-relaxed flex gap-2"><span className="text-gold/60">—</span> {it}</li>
                    ))}
                  </ul>
                </div>
              );
            })}
            {(result.related_slugs || []).length > 0 && (
              <div className="flex flex-wrap gap-2 pt-1">
                {result.related_slugs.map((slug) => (
                  <button key={slug} data-testid={`rights-related-${slug}`} onClick={() => navigate(`/rights/${slug}`)}
                    className="text-xs rounded-full border border-gold/30 text-gold px-3 py-1.5 hover:bg-gold/10">
                    {t("pages.rights.seeSheet")} {titleFor(slug)}
                  </button>
                ))}
              </div>
            )}
            {result.disclaimer && <p className="text-white/35 text-xs pt-2 border-t border-white/5">{result.disclaimer}</p>}
          </div>
        )}
      </div>

      {/* Fiches par catégorie */}
      {!data && <Spinner label={t("pages.rights.loadingSheets")} />}
      {data && data.categories.map((cat) => (
        <div key={cat} className="mb-8">
          <h2 className="font-display text-lg text-white/80 mb-3">{cat}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.sheets.filter((s) => s.category === cat).map((s) => {
              const Icon = ICONS[s.icon] || Scale;
              return (
                <button key={s.slug} data-testid={`rights-card-${s.slug}`} onClick={() => navigate(`/rights/${s.slug}`)}
                  className="card-surface p-5 text-left hover:border-gold/30 transition-colors group">
                  <div className="w-10 h-10 rounded-xl bg-gold/10 border border-gold/20 flex items-center justify-center mb-3">
                    <Icon size={20} className="text-gold" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-display text-base mb-1 group-hover:text-gold transition-colors">{s.title}</h3>
                  <p className="text-white/45 text-sm line-clamp-3 leading-relaxed">{s.summary}</p>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </Container>
  );
}
